import * as React from 'react';
import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';
import { PortCallUserRole, PortCallUserRoleDisplayName } from 'server/enums/PortCallUserRole';
import { OrgProfile } from './model';
import { OrgHeaderView } from './orgheaderview';
import { SelectProfileImageView } from 'portcall/selectprofileimage/view';
import { PostalAddressView } from 'portcall/postaladdress/view';
import { PhoneNumberView } from 'portcall/phonenumber/view';
import { ValidationErrorView } from 'core/validationerror/view';
import { BookingRateView } from 'portcall/bookingrate/view';
import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { InviteUsersView } from 'portcall/inviteusers/view';
import { UserPermissionsView } from 'portcall/userpermissions/view';
import * as cx from 'classnames';

export const OrgProfileView = SuperCritical(({ model: m } : { model : OrgProfile }) => 
    <div className="orgprofile pc-page-fixedwidth">
        {/*<loadingindicator params="request: Request" />*/}
        <div className="pc-actions-links-header">
            <a className="pc-back-link" onClick={noDefault(m.GoBack)}>&lt;&lt; Back</a>
        </div>
        {m.Organization() && <OrgHeaderView organization={m.Organization()!.Model} />}
        {m.Organization() && <SelectProfileImageView modelContainer={m.Organization()!.ImageSelector} />}
        <PageBody model={m} />
        <PageModals model={m} />
    </div>
);

const PageBody = SuperCritical(({ model: m } : { model : OrgProfile }) =>
    <div hidden={!!(m.Organization() && m.Organization()!.ImageSelector())}>
        <PageLeftSide model={m} />
        <ActionsSideBar model={m} />
    </div>
);

const PageLeftSide = SuperCritical(({ model: m } : { model : OrgProfile }) =>
    <div className="left-side">
        <div className={cx('pc-card profile-body', {
                'pc-show-editcontrols': m.Editing(), 
                'pc-show-viewcontrols': !m.Editing()
            })}>
            <div className="pc-card-header">
                <span className="pc-card-title">Org Information</span>
            </div>
            <div className="left-column">
                <OrgDetails model={m} />
                <OrgMembersView model={m} />
                <OrgBerthsView model={m} />
            </div>
            <div className="right-column">
                <BookingRateView bookingRate={m.Model.BookingRate} />
            </div>
        </div>
    </div>
);

const OrgDetails = SuperCritical(({ model: m } : { model : OrgProfile }) =>
    <div className="orgdetails">
        <h3 className="profile-section-title">Details</h3>
        {m.Model.CanEdit() &&
            <span>
                <a href="" className="pc-input-viewcontrol" onClick={noDefault(() => m.Editing(true))}>Edit</a>
                <a href="" className="pc-input-editcontrol" onClick={noDefault(() => m.Editing(false))}>Done Editing</a>
            </span>
        }
        {(org => org &&
            <div className="editprofileimagelink pc-input-editcontrol">
                <a href="" onClick={noDefault(org.ShowSelectProfileImage)}>
                    Edit Profile Image
                </a>
            </div>
        )(m.Organization())}
        <OrgNameView model={m} />
        <OrgAddressView model={m} />
        <OrgPhoneNumbersView model={m} />
    </div>
);

const OrgNameView = SuperCritical(({ model: m } : { model : OrgProfile }) =>
    <div className="name details-row">
        <label className="left-col">Name</label>
        <div className="right-col">
            <span className="pc-input-viewcontrol pc-text-fixedwidth">{m.Organization() && m.Organization()!.Model.Name()}</span>
            {(org => org &&
                <span>
                    <input className="pc-input-editcontrol"
                        type="text"
                        placeholder="Name"
                        value={org.Model.Name()}
                        onChange={e => org.Model.Name((e.target as HTMLInputElement).value)} />
                    <ValidationErrorView error={() => org.Validation()['Name']} />
                </span>
            )(m.Organization())}
        </div>
    </div>
);

const OrgAddressView = SuperCritical(({ model: m } : { model : OrgProfile }) =>
    <div className="address details-row">
        <label className="left-col">Address</label>
        {(org => org &&
            <div className="right-col">
                <div>
                    <a href="" className="pc-input-editcontrol profile-add-link" onClick={noDefault(org.PostalAddresses.Add)}>+Add</a>
                </div>
                <div className="postaladdresses">
                    {org.PostalAddresses.Addresses().map(addr =>
                        <PostalAddressView key={addr.Model.Id()} model={addr} />    
                    )}
                </div>
            </div>
        )(m.Organization())}
    </div>
);

const OrgPhoneNumbersView = SuperCritical(({ model: m } : { model : OrgProfile }) =>
    <div className="contacts details-row">
        <label className="left-col">Contacts</label>
        {(org => org &&
            <div className="right-col">
                <div className="link-shell">
                    <a href="" className="pc-input-editcontrol profile-add-link" onClick={noDefault(org.PhoneNumbers.Add)}>+Add</a>
                </div>
                <div className="phonenumbers">
                    {org.PhoneNumbers.PhoneNumbers().map(pn =>
                        <PhoneNumberView key={pn.Model.Id()} model={pn} />
                    )}
                </div>
            </div>
        )(m.Organization())}
    </div>
);

const OrgMembersView = SuperCritical(({ model: m } : { model : OrgProfile }) =>
    <div className="orgmembers">
        <h3 className="profile-section-title">
            Members
            {' '}
            {m.Organization() ? '(' + m.Organization()!.Users().length + ')' : ''}
        </h3>
        {m.Model.CanManageUsers() &&
            <span>
                <a href="" onClick={noDefault(m.ShowUserPermissions)}>Manage</a>
                <span className="pc-icon-bullet"></span>
                <a href="" onClick={noDefault(m.ShowInviteUsers)}>Invite</a>
            </span>
        }
        {(org => org &&
            <div>
                {org.Users().map(user => 
                    <div className="member" key={user.Model.Id()}>
                        <img src={user.Model.Image.ThumbUrl()} />
                        <a href="" className="name-text pc-text-fixedwidth pc-text-smalltext" onClick={noDefault(user.ToggleProfile)}>
                            {user.Model.FullName()}
                        </a>
                        <PersonProfilePopupView modelContainer={user.ProfilePopup} />
                    </div>
                )}
            </div>
        )(m.Organization())}
    </div>
);

const OrgBerthsView = SuperCritical(({ model: m } : { model : OrgProfile }) => {
    if (m.Berths().length === 0) return null!;
    return (
        <div className="orgberths">
            <h3 className="profile-section-title">
                Berths ({m.Berths().length})
            </h3>
            {m.Model.CanAddBerth() &&
                <a href="" className="addberth-link" onClick={noDefault(m.AddBerth.Add)}>+ Add Berth</a>
            }
            {m.Ports().map(port =>
                <div className="port" key={port.Model.Id()}>
                    {port.PierGroups().map(pg =>
                        <div className="pier-group" key={pg.Model.Id()}>
                            {pg.Berths().map(b =>
                                <div className="berth" key={b.Id()}>
                                    <a href={'#!berthprofile?id=' + b.Id()}>
                                        {port.Model.Name() + ' \u2014 ' + (b.Location() ? b.Location()!.Name() : '')}
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

const ActionsSideBar = SuperCritical(({ model: m } : { model : OrgProfile }) => {
    if (!m.Model.CanManageBerths() || m.Berths().length === 0) return null;
    return(
        <div className="right-side">
            <div className="pc-card profile-sidebar">
                <div className="pc-card-header">
                    <span className="pc-card-title">Berth Manager Settings</span>
                </div>
                <div className="harbormaster-setting">
                    <a href={`#!berthmgrdashboard?id=${m.Id}`}>Berth Manager's Dashboard</a>
                    <div className="text-details-blurb">
                        Manage reservations and services at your berths.
                    </div>
                </div>
                <div className="harbormaster-setting">
                    <a href={`#!reservationsdashboard?orgId=${m.Id}&role=${PortCallUserRoleDisplayName[PortCallUserRole.SchedulingAuthority]}`}>Scheduler's Report</a>
                    <div className="text-details-blurb">
                        View list of reservations for which this organization is the Scheduling Authority.
                    </div>
                </div>
                <div className="harbormaster-setting">
                    <a href={`#!batchesdashboard?orgId=${m.Id}&role=${PortCallUserRoleDisplayName[PortCallUserRole.SchedulingAuthority]}`}>Scheduler's Batch</a>
                    <div className="text-details-blurb">
                        View batches of reservations for which this organization is the Scheduling Authority.
                    </div>
                </div>
                <div className="harbormaster-setting">
                    <a href={`#!berthclosuresdashboard?orgId=${m.Id}`}>Berth Closures</a>
                    <div className="text-details-blurb">
                        View closures at your berths.
                    </div>
                </div>
                <div className="harbormaster-setting">
                    <a href={'#!topiccardtemplates?id=' + m.Id}>Topic Cards</a>
                    <div className="text-details-blurb">
                        Create cards for pier-side services, berth manager approval, and other operational requirements.
                    </div>
                </div>
                <div className="harbormaster-setting">
                    <a href={'#!amenitylistmgmt?id=' + m.Id}>Amenities List</a>
                    <div className="text-details-blurb">
                        Configure amenities offered at your berths.
                    </div>
                </div>
                <div className="harbormaster-setting">
                    <a href={'#!tarifflookups?id=' + m.Id}>Tariff Activities</a>
                    <div className="text-details-blurb">
                        Configure tariff related activities, such as trade routes and cargo categories, at your berths.
                    </div>
                </div>
                <div className="harbormaster-setting">
                    <a href={'#!configurereminders?id=' + m.Id}>Reminders</a>
                    <div className="text-details-blurb">
                        Specify reminder dates for scheduled PortCalls.
                    </div>
                </div>
            </div>
        </div>
    );
});

const PageModals = SuperCritical(({ model: m } : { model : OrgProfile }) =>
    <div>
        <UserPermissionsView modelContainer={m.UserPermissions} />
        <InviteUsersView modelContainer={m.InviteUsers} />
    </div>
);



// WEBPACK FOOTER //
// ./apps/portcall/orgprofile/view.tsx